import React, {useCallback, useMemo} from 'react';

import {Analytics} from '../../../../../services/analytics';
import {useMemoizedSelector} from '../../../../../state/hooks';
import {useRunsData, useRunSetsQuery} from '../../../../../state/runs/hooks';
import * as ViewHooks from '../../../../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../../../../state/views/panelBankSectionConfig/actions';
import {useSingleRunModeContext} from '../../../../MultiRunWorkspace/SingleRunMode';
import {
  type MediaBrowserPanelConfig,
  transformOverlayQuery,
} from '../../../../PanelMediaBrowser';
import {
  getConfigWithDefaults,
  useAllRuns,
  useOverlays,
} from '../../../../PanelMediaBrowser/useMediaPanelValues';
import {usePanelsSharedRefsContext} from '../../../../Views/contexts/PanelsSharedRefsContext';
import {useMaybeWorkspaceRefsContext} from '../../../../Workspace/WorkspaceRefsContext';
import {
  useCascadingSectionMediaSliderKey,
  useSectionMediaKeysAndCardTypes,
} from '../../hooks';
import {useSectionSettingsContext} from '../../SectionSettingsContextProvider';
import {
  makeCascadingPanelMediaConfigSelector,
  selectCustomRunColors,
} from '../mediaSettingsSelectors';
import {type ContextProviderState, SettingsLevel} from '../types';
import {MediaSettingsContextProvider} from './MediaSettingsContextProvider';
import {useMediaSectionOverrides, useRampFlagsForMediaSettings} from './utils';

/**
 * Provider for section-level media settings.
 *
 * This provider wraps MediaSettingsContextProvider and internally uses
 * useSectionMediaSettingsDrawer to compute the context value.
 *
 * Usage:
 * ```tsx
 * <MediaSectionSettingsContextProvider>
 *   <MediaSettingsDrawerContent ... />
 * </MediaSectionSettingsContextProvider>
 * ```
 */
export function MediaSectionSettingsContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const contextValue = useSectionMediaSettingsDrawer();
  return (
    <MediaSettingsContextProvider contextValue={contextValue}>
      {children}
    </MediaSettingsContextProvider>
  );
}

/**
 * Internal hook - do not use directly.
 * Use MediaSectionSettingsContextProvider instead and use useMediaSettingsContext, useMediaSettingsActions, or useMediaSettingsState to access the context value(s).
 */
const useSectionMediaSettingsDrawer = (): ContextProviderState => {
  const {sectionMediaSettings, sectionRef} = useSectionSettingsContext();
  const {mediaCardTypes, mediaKeys} = useSectionMediaKeysAndCardTypes();
  const {customRunColorsRef, runSetRefs, customRunNamesRef} =
    usePanelsSharedRefsContext();
  const customRunColors = useMemoizedSelector(
    selectCustomRunColors,
    customRunColorsRef
  );
  const query = useRunSetsQuery({runSetRefs, customRunNamesRef});
  const {isSingleRun} = useSingleRunModeContext();
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  // no panel ref: cascade only workspace → section values
  const cascadedMediaSettings = useMemoizedSelector(
    makeCascadingPanelMediaConfigSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    undefined
  );
  const mediaSliderKey = useCascadingSectionMediaSliderKey();

  const configWithDefaults = getConfigWithDefaults(
    cascadedMediaSettings,
    mediaSliderKey,
    isSingleRun
  );
  // Load class-label metadata for a sample of runs so we know which overlay
  // settings (masks, bounding boxes) to show.
  const panelQuery = useMemo(() => transformOverlayQuery(query), [query]);
  const {data, loading} = useRunsData(panelQuery);

  const allRuns = useAllRuns({
    data,
    query: panelQuery,
    customRunColors,
    pageQuery: query,
  });

  const {allMasks, hasMasks, allBoxes, hasBoxes} = useOverlays({
    allRuns,
    mediaKeys,
  });
  const rampFlags = useRampFlagsForMediaSettings();

  const updateConfig = ViewHooks.useViewAction(
    sectionRef,
    PanelBankSectionConfigActions.updateSectionMediaSettings
  );

  const unsetConfigFields = ViewHooks.useViewAction(
    sectionRef,
    PanelBankSectionConfigActions.unsetSectionMediaSettings
  );

  const {numberOfOverridesBelow, clearOverridesBelow} =
    useMediaSectionOverrides(sectionRef);

  const updateConfigAndLog = useCallback(
    (configUpdate: Partial<MediaBrowserPanelConfig>) => {
      updateConfig(configUpdate);
      Analytics.track('Update Section Media Settings', {
        configUpdate: JSON.stringify(configUpdate),
      });
    },
    [updateConfig]
  );

  const unsetConfigFieldsAndLog = useCallback(
    (fields: Array<keyof MediaBrowserPanelConfig>) => {
      unsetConfigFields(fields);
      // `field` keeps the historical string shape for existing queries;
      // `fields` carries the structured list.
      Analytics.track('Unset Section Media Setting', {
        field: fields.join(','),
        fields,
      });
    },
    [unsetConfigFields]
  );

  const unsetConfigFieldAndLog = useCallback(
    (field: keyof MediaBrowserPanelConfig) => unsetConfigFieldsAndLog([field]),
    [unsetConfigFieldsAndLog]
  );

  const clearOverridesBelowAndLog = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      clearOverridesBelow(field);
      Analytics.track('Clear Panel Media Setting Overrides From Section', {
        field,
        numberOfOverrides: numberOfOverridesBelow[field],
      });
    },
    [clearOverridesBelow, numberOfOverridesBelow]
  );

  return useMemo(
    () => ({
      config: configWithDefaults,
      configWithoutDefaults: sectionMediaSettings,
      hasMasks,
      hasBoundingBoxes: hasBoxes,
      loading,
      rampFlags,
      mediaCardTypes,
      updateConfig: updateConfigAndLog,
      unsetConfigField: unsetConfigFieldAndLog,
      unsetConfigFields: unsetConfigFieldsAndLog,
      allBoxes,
      // We'd have to fetch all the files for all the section's runs to determine the min and max values
      // for the slider for each of the masks. Since this is section level, let's just handle non-existing mins and maxes properly in ConfidenceSlider.tsx.
      sliderRanges: null,
      allMasks,
      numberOfOverridesBelow: {
        panel: numberOfOverridesBelow,
      },
      clearOverridesBelow: {
        panel: clearOverridesBelowAndLog,
      },
      settingsLevel: SettingsLevel.SECTION,
    }),
    [
      mediaCardTypes,
      configWithDefaults,
      sectionMediaSettings,
      hasBoxes,
      hasMasks,
      loading,
      allBoxes,
      allMasks,
      updateConfigAndLog,
      unsetConfigFieldAndLog,
      unsetConfigFieldsAndLog,
      numberOfOverridesBelow,
      clearOverridesBelowAndLog,
      rampFlags,
    ]
  );
};
